/**
 * Manos F7-P4 — interstitial modal over a dimmed page. Accessibility-correct (F7-AC-05):
 * focus trap, dismiss via Esc / backdrop / button, focus restored to the previously-focused
 * element on close. Session-capping (once per visitor) is the PARENT's responsibility (via
 * useSessionFlag) — this component just renders + closes. Primary CTA routes to the Centro
 * de Crisis (F1 at /venezuela). Visible-face policy (D8) enforced by the manos i18n copy.
 */
import { useCallback, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import type { Translations } from '../../types';

const CRISIS_HREF = '/venezuela';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export function ManosModal({ t, onClose }: { t: Translations; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLAnchorElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const focusables = useCallback((): HTMLElement[] => {
    const root = dialogRef.current;
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  }, []);

  useEffect(() => {
    // Remember what had focus, move focus into the dialog (primary CTA).
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    primaryRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      // Focus trap: keep Tab/Shift+Tab cycling within the dialog.
      const els = focusables();
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Restore focus to the element that had it before the modal opened.
      previouslyFocused.current?.focus();
    };
  }, [onClose, focusables]);

  return (
    <div
      data-testid="manos-modal-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        // Backdrop (but not the dialog) dismisses.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        data-testid="manos-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="manos-modal-title"
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t.manos.modalClose}
          className="absolute right-3 top-3 p-1 rounded hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <h2 id="manos-modal-title" className="pr-6 text-lg font-bold text-vinotinto-dark">
          {t.manos.modalTitle}
        </h2>
        <p className="mt-2 text-sm text-gray-700">{t.manos.modalBody}</p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
          <a
            ref={primaryRef}
            href={CRISIS_HREF}
            className="rounded-lg bg-vinotinto px-4 py-2 text-center text-sm font-medium text-white hover:bg-vinotinto-dark transition-colors"
          >
            {t.manos.modalPrimary}
          </a>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-center text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            {t.manos.modalDismiss}
          </button>
        </div>
      </div>
    </div>
  );
}
